import * as THREE from "three";
import type { BodyState } from "../physics/body";

/* =====================================================================
   CAMERA
   'camT' is where the camera should be; 'cam' eases toward it every frame,
   so orbiting, zooming and panning all glide smoothly. The camera orbits a
   focus point that can be moved anywhere on the grid.
   ===================================================================== */
const HOME = { theta: 0.7, phi: 1.05, dist: 30, fx: 0, fz: 0 };
export const PHI_MIN = 0.1,
  PHI_MAX = 1.45,
  DIST_MIN = 5,
  DIST_MAX = 80;
const EDGE = 12;

export type CameraRig = ReturnType<typeof createCameraRig>;

export function createCameraRig(camera: THREE.PerspectiveCamera, sun: THREE.DirectionalLight) {
  const camT = { ...HOME };
  const cam = { ...HOME };
  const camLook = new THREE.Vector3(0, 1, 0);
  let camHeight = 0; // smoothed height of settled blocks near the focus point
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();

  function clampFocus() {
    camT.fx = THREE.MathUtils.clamp(camT.fx, -EDGE, EDGE);
    camT.fz = THREE.MathUtils.clamp(camT.fz, -EDGE, EDGE);
  }
  function viewDistance() {
    return cam.dist + camHeight * 0.7;
  }
  function orbitBy(dx: number, dy: number) {
    camT.theta -= dx * 0.008;
    camT.phi = THREE.MathUtils.clamp(camT.phi - dy * 0.006, PHI_MIN, PHI_MAX);
  }
  // Pan so the grid follows the pointer: convert pixels to metres at the
  // current distance, then move along the camera's horizontal right/forward axes
  function panBy(dx: number, dy: number) {
    const wpp =
      (2 * viewDistance() * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2))) / innerHeight;
    const cs = Math.cos(cam.theta),
      sn = Math.sin(cam.theta);
    const vf = 1 / Math.max(Math.cos(cam.phi), 0.35); // screen-vertical covers more ground when tilted
    camT.fx += (-cs * dx - sn * dy * vf) * wpp;
    camT.fz += (sn * dx - cs * dy * vf) * wpp;
    clampFocus();
  }
  /** The raycaster aimed from the camera through a screen point. */
  function raycast(x: number, y: number) {
    ndc.set((x / innerWidth) * 2 - 1, -(y / innerHeight) * 2 + 1);
    raycaster.setFromCamera(ndc, camera);
    return raycaster;
  }
  function screenToPlane(x: number, y: number, planeY: number) {
    const hit = new THREE.Vector3();
    return raycast(x, y).ray.intersectPlane(
      new THREE.Plane(new THREE.Vector3(0, 1, 0), -planeY),
      hit,
    )
      ? hit
      : null;
  }
  // Zoom toward a screen point: the spot under the cursor stays under the cursor
  function zoomAt(factor: number, x?: number, y?: number) {
    const before = camT.dist;
    camT.dist = THREE.MathUtils.clamp(before * factor, DIST_MIN, DIST_MAX);
    const f = camT.dist / before;
    const p = x === undefined || y === undefined ? null : screenToPlane(x, y, camLook.y);
    if (p) {
      camT.fx = p.x + (camT.fx - p.x) * f;
      camT.fz = p.z + (camT.fz - p.z) * f;
      clampFocus();
    }
  }
  function focusOn(p: THREE.Vector3) {
    camT.fx = p.x;
    camT.fz = p.z;
    clampFocus();
  }
  function fitAll(blocks: readonly BodyState[]) {
    let x0 = Infinity,
      x1 = -Infinity,
      z0 = Infinity,
      z1 = -Infinity,
      y1 = 0;
    for (const b of blocks) {
      if (b.pos.y < -1.5) continue;
      const a = b.aabb;
      x0 = Math.min(x0, a.lowerBound.x);
      x1 = Math.max(x1, a.upperBound.x);
      z0 = Math.min(z0, a.lowerBound.z);
      z1 = Math.max(z1, a.upperBound.z);
      y1 = Math.max(y1, a.upperBound.y);
    }
    if (x0 === Infinity) {
      resetView();
      return;
    }
    camT.fx = (x0 + x1) / 2;
    camT.fz = (z0 + z1) / 2;
    clampFocus();
    camT.dist = THREE.MathUtils.clamp(Math.max(x1 - x0, z1 - z0, y1) * 1.4 + 8, DIST_MIN, DIST_MAX);
  }
  function topView() {
    camT.phi = PHI_MIN;
  }
  function resetView() {
    Object.assign(camT, HOME);
  }

  // The camera follows settled blocks near its focus point, so falling blocks
  // and towers elsewhere on the grid never move the view
  function follow(dt: number, localTop: number) {
    camHeight += (localTop - camHeight) * Math.min(1, dt * 1.5);
  }

  // Smooth camera: ease the current view toward the target view
  function update(dt: number) {
    const k = 1 - Math.exp(-dt * 10);
    for (const key of ["theta", "phi", "dist", "fx", "fz"] as const)
      cam[key] += (camT[key] - cam[key]) * k;
    const lookY = Math.max(1, camHeight * 0.6);
    camLook.set(cam.fx, camLook.y + (lookY - camLook.y) * Math.min(1, dt * 3), cam.fz);
    const r = viewDistance();
    camera.position.set(
      camLook.x + r * Math.sin(cam.phi) * Math.sin(cam.theta),
      camLook.y + r * Math.cos(cam.phi),
      camLook.z + r * Math.sin(cam.phi) * Math.cos(cam.theta),
    );
    camera.lookAt(camLook);

    sun.position.set(cam.fx + 10, camLook.y + 22, cam.fz + 8);
    sun.target.position.set(cam.fx, camLook.y, cam.fz);
  }

  return {
    cam,
    camT,
    viewDistance,
    orbitBy,
    panBy,
    raycast,
    screenToPlane,
    zoomAt,
    focusOn,
    fitAll,
    topView,
    resetView,
    follow,
    update,
  };
}
